import { BrandAsset } from "../../../foundations/brand-assets/BrandAsset";
import { MediaQuery } from "../../../utils/MediaQuery";
import {
  restoreDefaultFavicon,
  updateFavicon,
} from "../../../utils/FaviconUtils";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useScheme } from "../../../hooks/useColors";
import { useTranslation } from "react-i18next";
import { css, cx } from "@emotion/css";

type NavLogoVariant = "logo" | "full";

const NavLogoSlotContext = createContext<NavLogoVariant | null>(null);

export function NavLogoSlotProvider(props: {
  slot: NavLogoVariant;
  children: ReactNode;
}) {
  const { slot, children } = props;
  return (
    <NavLogoSlotContext.Provider value={slot}>
      {children}
    </NavLogoSlotContext.Provider>
  );
}

interface Props {
  type?: NavLogoVariant;
  logoUrl?: string | null;
  darkLogoUrl?: string | null;
  orgIconUrl?: string | null;
  orgIconUrlDarkMode?: string | null;
}

export function NavLogo(props: Props) {
  const { t } = useTranslation();
  const { logoUrl, darkLogoUrl, orgIconUrl, orgIconUrlDarkMode, type } = props;
  const navLogoSlot = useContext(NavLogoSlotContext);
  const variant = type ?? navLogoSlot;
  const scheme = useScheme();
  const { enableCobrandingForApplications } = useFlags();

  useEffect(() => {
    if (enableCobrandingForApplications && orgIconUrl) {
      updateFavicon(orgIconUrl);
    } else {
      restoreDefaultFavicon();
    }
  }, [orgIconUrl, enableCobrandingForApplications]);

  if (enableCobrandingForApplications) {
    const isDarkMode = scheme === "dark";

    const smMdSrc = isDarkMode
      ? orgIconUrlDarkMode || orgIconUrl || undefined
      : orgIconUrl || orgIconUrlDarkMode || undefined;

    const lgSrc = isDarkMode
      ? darkLogoUrl || logoUrl || undefined
      : logoUrl || darkLogoUrl || undefined;

    const smMdUsesLightOnDark = Boolean(
      isDarkMode && !orgIconUrlDarkMode && orgIconUrl
    );
    const lgUsesLightOnDark = Boolean(isDarkMode && !darkLogoUrl && logoUrl);

    const logo = (
      <ClientLogo
        src={smMdSrc}
        alt={t("Client logo")}
        className={cx(
          styles.logo,
          smMdUsesLightOnDark && styles.logoDarkCompat
        )}
        fallback={
          <BrandAsset brand="certn" type="logo" height="100%" width="100%" />
        }
      />
    );

    const full = (
      <ClientLogo
        src={lgSrc}
        alt={t("Client logo")}
        className={cx(styles.logo, lgUsesLightOnDark && styles.logoDarkCompat)}
        fallback={
          <BrandAsset brand="certn" type="full" height="100%" width="100%" />
        }
      />
    );

    if (variant === "logo") {
      return logo;
    }

    if (variant === "full") {
      return full;
    }

    return (
      <>
        <MediaQuery type="show" sm md>
          {logo}
        </MediaQuery>
        <MediaQuery type="hide" sm md>
          {full}
        </MediaQuery>
      </>
    );
  }

  return (
    <>
      <MediaQuery type="show" sm md>
        <BrandAsset brand="certn" type="logo" height="100%" width="100%" />
      </MediaQuery>
      <MediaQuery type="hide" sm md>
        <BrandAsset brand="certn" type="full" height="100%" width="100%" />
      </MediaQuery>
    </>
  );
}

function ClientLogo({
  src,
  alt,
  className,
  fallback,
}: {
  src?: string;
  alt: string;
  className?: string;
  fallback: ReactNode;
}) {
  const [failedSrc, setFailedSrc] = useState<string>();

  if (!src || failedSrc === src) {
    return fallback;
  }

  return (
    <img
      alt={alt}
      src={src}
      className={className}
      onError={() => setFailedSrc(src)}
    />
  );
}

const styles = {
  logo: css({
    display: "block",
    height: "100%",
    width: "auto",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  }),
  logoDarkCompat: css({
    backgroundColor: "white",
    padding: "var(--space-05)",
    borderRadius: "var(--border-radius-1)",
    boxSizing: "border-box",
  }),
};
